/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LoggingSettingsFormView.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Form view for FreeSWITCH logging settings (Console and File Logging).

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Code } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useDebugMode } from '../../hooks/useDebugMode';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { useLoggingSettingsData } from './hooks/useLoggingSettingsData';
import { useLoggingSettingsForm } from './hooks/useLoggingSettingsForm';
import { useLoggingSettingsSubmit } from './hooks/useLoggingSettingsSubmit';
import { LoggingSettingsFormFields } from './components/LoggingSettingsFormFields';
import { XmlPreviewModal } from './components/XmlPreviewModal';
import { DEFAULT_CONSOLE_SETTINGS, DEFAULT_FILE_LOGGING } from './types';

export function LoggingSettingsFormView() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { isActive: isDebugModeActive } = useDebugMode();
  useFixedActionBar(true);

  const { loading, formData, setFormData } = useLoggingSettingsData();
  const {
    updateConsoleSettings,
    updateFileLogging,
    updateProfile,
    updateProfileMapping,
    updateEnabled
  } = useLoggingSettingsForm(formData, setFormData);
  const { handleSave, saving } = useLoggingSettingsSubmit(formData, setFormData);

  const [xmlModal, setXmlModal] = useState<{ isOpen: boolean; type: 'console' | 'file' | null }>({
    isOpen: false,
    type: null
  });

  const handleReset = useCallback(() => {
    setFormData({
      domainId: formData.domainId,
      consoleSettings: DEFAULT_CONSOLE_SETTINGS,
      fileLogging: DEFAULT_FILE_LOGGING,
      enabled: true
    });
  }, [formData.domainId, setFormData]);

  const handleViewXml = useCallback(() => {
    const defaultType = formData.consoleXml ? 'console' : (formData.fileXml ? 'file' : 'console');
    setXmlModal({ isOpen: true, type: defaultType });
  }, [formData.consoleXml, formData.fileXml]);

  const handleDownloadXml = useCallback((xml: string, filename: string) => {
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        title={t('loggingSettings.title')}
        icon={<Settings className="h-5 w-5" />}
        backButtonText={t('common.exit')}
        onBack={() => navigate('/admin/system-settings')}
        actions={[
          // Only show XML preview button when debug mode is active
          ...(isDebugModeActive && (formData.consoleXml || formData.fileXml)
            ? [{
              icon: <Code className="h-4 w-4" />,
              tooltip: t('loggingSettings.buttons.viewXml'),
              onClick: handleViewXml
            }]
            : [])
        ]}
      />

      <LoggingSettingsFormFields
        formData={formData}
        updateConsoleSettings={updateConsoleSettings}
        updateFileLogging={updateFileLogging}
        updateProfile={updateProfile}
        updateProfileMapping={updateProfileMapping}
        updateEnabled={updateEnabled}
      />

      <XmlPreviewModal
        isOpen={xmlModal.isOpen}
        type={xmlModal.type}
        consoleXml={formData.consoleXml}
        fileXml={formData.fileXml}
        onClose={() => setXmlModal({ isOpen: false, type: null })}
        onTypeChange={(type) => setXmlModal(prev => ({ ...prev, type }))}
        onDownload={handleDownloadXml}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSave={handleSave}
        onCancel={handleReset}
        saveText={saving ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
        isSaving={saving}
        saveDisabled={saving}
      />
    </div>
  );
}

